"use client";

import { cn } from "@/lib/utils";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { TooltipContentProps } from "@radix-ui/react-tooltip";
import * as React from "react";

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipPortal = TooltipPrimitive.Portal;

const commonStyle =
  "z-50 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2";

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    disablePortal?: boolean;
  }
>(({ className, sideOffset = 4, disablePortal = false, ...props }, ref) => {
  const content = (
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        commonStyle,
        "rounded-2xl border bg-popover p-3 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 overflow-hidden",
        className
      )}
      {...props}
    />
  );
  if (!disablePortal) {
    return <TooltipPortal>{content}</TooltipPortal>;
  }
  return content;
});

export function TooltipContentWithArrow({
  className,
  sideOffset = 0,
  children,
  color,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & {
  color?: string;
}) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          "bg-foreground text-background animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5 text-xs text-balance",
          className
        )}
        style={color ? { backgroundColor: color } : undefined}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow
          className="bg-foreground fill-foreground z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]"
          style={color ? { backgroundColor: color, fill: color } : undefined}
        />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export const SimpleTooltip = React.forwardRef<
  HTMLButtonElement,
  {
    children: React.ReactNode;
    style?: React.CSSProperties;
    content: React.ReactNode;
    asChild?: boolean;
    side?: TooltipContentProps["side"];
    className?: string;
    sideOffset?: number;
    delayDuration?: number;
    contentClassName?: string;
    disableTooltip?: boolean;
    disablePortal?: boolean;
    disableHoverableContent?: boolean;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
  } & Omit<
    React.ComponentPropsWithoutRef<typeof TooltipTrigger>,
    "asChild" | "className" | "style" | "content"
  >
>(
  (
    {
      children,
      style,
      content,
      asChild = false,
      side,
      className,
      contentClassName,
      sideOffset,
      delayDuration,
      disableTooltip = false,
      disablePortal = false,
      disableHoverableContent = false,
      open,
      onOpenChange,
      ...rest
    },
    ref
  ) => {
    if (disableTooltip) {
      return children;
    }
    return (
      <Tooltip
        open={open}
        onOpenChange={onOpenChange}
        delayDuration={delayDuration}
        disableHoverableContent={disableHoverableContent}
      >
        <TooltipTrigger
          ref={ref}
          asChild={asChild}
          className={className}
          style={style}
          {...rest}
        >
          {children}
        </TooltipTrigger>
        <TooltipContent
          className={cn("z-[1000] text-xs max-w-80 m-1", contentClassName)}
          side={side}
          sideOffset={sideOffset}
          disablePortal={disablePortal}
        >
          {content}
        </TooltipContent>
      </Tooltip>
    );
  }
);

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
